import styled from "styled-components";
import CircularProgress from "@material-ui/core/CircularProgress";

import { ButtonProps } from "../../ecommerce/models";
import useCompanyStore from "../../Store/supplierCompany/companyData";

/**
 * S4: colors used when the provider did not save its brand colors yet, so no
 * module changes its look just because the store is empty.
 */
export const FALLBACK_PRIMARY_COLOR = "#f8bd43";
export const FALLBACK_SECONDARY_COLOR = "#252525";

/** S4: label colors of the brand variants. */
export const DARK_TEXT_COLOR = "#252525";
export const LIGHT_TEXT_COLOR = "#FFFFFF";

const HEX_COLOR_PATTERN = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

/**
 * S4: relative luminance where the contrast against `#252525` equals the
 * contrast against `#FFFFFF`:
 * `(0.2182 + 0.05) / (0.0185 + 0.05) ≈ 1.05 / (0.2182 + 0.05)`.
 * Above it the dark label wins, below it the light one does, so the derived
 * label is always the one with the best contrast (WCAG).
 */
const DARK_TEXT_LUMINANCE_THRESHOLD = 0.2182;

/** Six hex digits without `#`, or `null` when the value is not a hex color. */
const normalizeHexColor = (value: string): string | null => {
  const trimmedValue = value.trim();

  if (!HEX_COLOR_PATTERN.test(trimmedValue)) {
    return null;
  }

  const digits = trimmedValue.slice(1).toLowerCase();

  return digits.length === 3
    ? digits
        .split("")
        .map((digit) => `${digit}${digit}`)
        .join("")
    : digits;
};

/** S4: `true` for a `#rgb`/`#rrggbb` value, `false` for anything else. */
export const isHexColor = (value: unknown): value is string =>
  typeof value === "string" && normalizeHexColor(value) !== null;

/** S4: brand color from the store, or the current color when it is not hex. */
export const resolveBrandColor = (
  storedColor: string | null | undefined,
  fallbackColor: string
): string => (isHexColor(storedColor) ? storedColor.trim() : fallbackColor);

const linearizeChannel = (channel: number) => {
  const normalizedChannel = channel / 255;

  return normalizedChannel <= 0.03928
    ? normalizedChannel / 12.92
    : Math.pow((normalizedChannel + 0.055) / 1.055, 2.4);
};

/**
 * S4: WCAG relative luminance of a hex color
 * (`0.2126 * R + 0.7152 * G + 0.0722 * B` over the linearized channels), or
 * `null` when the value is not a hex color.
 */
export const relativeLuminance = (hexColor: string): number | null => {
  const normalizedColor = normalizeHexColor(hexColor);

  if (!normalizedColor) {
    return null;
  }

  const [red, green, blue] = [0, 2, 4].map((offset) =>
    linearizeChannel(parseInt(normalizedColor.slice(offset, offset + 2), 16))
  );

  return 0.2126 * red + 0.7152 * green + 0.0722 * blue;
};

/**
 * S4: label color for a brand background. An invalid or empty color never
 * throws nor produces `NaN`: it resolves to the dark label, which is also the
 * label of the primary fallback.
 */
export const readableTextColorFor = (backgroundHexColor: string): string => {
  const luminance = relativeLuminance(backgroundHexColor);

  if (luminance === null) {
    return DARK_TEXT_COLOR;
  }

  return luminance >= DARK_TEXT_LUMINANCE_THRESHOLD
    ? DARK_TEXT_COLOR
    : LIGHT_TEXT_COLOR;
};

type StyledButtonProps = ButtonProps & {
  companyPrimaryColor: string;
  companySecondaryColor: string;
};

const CustomButton = ({
  disabled,
  children,
  color,
  shape,
  icon: Icon,
  size = "medium",
  isLoading,
  block,
  className,
  onClick,
  type,
  ...rest
}: ButtonProps) => {
  // S4: `primary` and `secondary` follow the provider brand colors, exactly as
  // `EcommerceActionButton/Button.tsx` already does. The semantic variants
  // (`success`, `warning`, `danger`, `outlined`) keep their own meaning.
  const { companyPrimaryColor, companySecondaryColor } = useCompanyStore();

  return (
    <StyledButton
      {...rest}
      type={type}
      color={color}
      shape={shape}
      size={size}
      disabled={disabled}
      onClick={onClick}
      isLoading={isLoading}
      className={className}
      block={block}
      companyPrimaryColor={resolveBrandColor(
        companyPrimaryColor,
        FALLBACK_PRIMARY_COLOR
      )}
      companySecondaryColor={resolveBrandColor(
        companySecondaryColor,
        FALLBACK_SECONDARY_COLOR
      )}
    >
      {isLoading ? (
        <>
          <CircularProgress size={20} color="inherit" />
          <div style={{ visibility: "hidden" }}>
            {Icon && <Icon />}
            <span>{children}</span>
          </div>
        </>
      ) : (
        <>
          {Icon && <Icon />}
          <span>{children}</span>
        </>
      )}
    </StyledButton>
  );
};

const StyledButton = styled.button<StyledButtonProps>`
  /* Flexbox styles */
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: "Poppins", sans-serif;
  ${(props) => props.block && `width: 100%;`}

  /* Dimension styles */
  padding: 12px 24px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  svg {
    margin-right: 8px;
    font-size: 1.2rem;
  }
  @media (max-width: 768px) {
    svg {
      display: none;
    }
  }

  /* Font styles */
  font-size: 0.875rem /* 14px */;
  line-height: 1.25rem /* 20px */;
  font-weight: 500;

  /* Color styles */
  border: 0;
  color: #000000;
  cursor: pointer;

  /* Pseudo-element styles */
  transition: all 0.2s ease;

  :hover {
    /* Hover effect */
    filter: brightness(1.07);
  }

  /* Circular progress style */
  .MuiCircularProgress-root {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    /* Remove hover effect when disabled */
    :hover {
      filter: none;
    }
  }

  /* Adapt the colors */
  ${(props) =>
    props.color === "primary" &&
    `border: none; background-color: ${props.companyPrimaryColor}; color: ${readableTextColorFor(
      props.companyPrimaryColor
    )};`}
  ${(props) =>
    props.color === "secondary" &&
    `border: none; background-color: ${props.companySecondaryColor}; color: ${readableTextColorFor(
      props.companySecondaryColor
    )}; &&:hover {opacity: 0.9;}`}
  ${(props) =>
    props.color === "success" &&
    `border: none; background-color: #52BE80; color: #FFFFFF;`}
  ${(props) =>
    props.color === "warning" &&
    `border: none; background-color: #FFD966; color: #252525;`}
  ${(props) =>
    props.color === "danger" &&
    `border: none; background-color: #EC7063; color: #FFFFFF;`}
  ${(props) =>
    props.color === "outlined" &&
    `border: 1px solid rgba(0,0,0,0.5); box-shadow: none; background-color: transparent; color: #252525; &&:hover {background-color: #e4e4e4; color: #252525;}`}

  /* Adapt size of icon */
  ${(props) =>
    props.size === "small" &&
    `svg {
      font-size: 1.3rem;
    }`}
  ${(props) =>
    props.size === "medium" &&
    `svg {
      font-size: 1.5rem;
    }`}
  ${(props) =>
    props.size === "big" &&
    `svg {
      font-size: 1.7rem;
    }`}

  /* Adapt the shape */
  ${(props) => props.shape === "little-rounded" && `border-radius: 4px;`}
  ${(props) => props.shape === "pill" && `border-radius: 25px;`}

  /* Adapt the size */
  ${(props) => props.size === "small" && `font-size: 12px; padding: 8px 16px; `}
  ${(props) =>
    props.size === "medium" && `font-size: 14px; padding: 12px 24px;`}
  ${(props) => props.size === "big" && `font-size: 16px; padding: 16px 32px;`}
`;

export default CustomButton;
